// Iter-200f / iter-206j — "Powered by BAIM" promo strip + screenshot carousel.
// Shown on the public payment-declare page:
//   • Compact strip (variant="compact")  -> below the form, above the legal footer.
//   • Expanded section (variant="expanded") -> on the success page after a
//     declaration is submitted, with the full screenshot carousel + CTA.
//
// Iter-206j updates:
//   • New screenshot order matching the actual modules of the platform
//     (10 captures incl. NDF/OCR, BC, chatbot, simulation…).
//   • Each capture is clickable → opens a full-screen lightbox preview.
//   • All download affordances are disabled (no right-click, no drag, no
//     long-press menu, no native download button) — these are confidential
//     demo screenshots.
import React, { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Mail, ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

const BRAND = {
  navy: "#1E3A5F",
  navyHover: "#2A4570",
  creamBg: "#FAF6EE",
  creamBg2: "#F5EBD6",
  creamBorder: "#EDE0C6",
  orange: "#D97A2A",
};

const DEMO_EMAIL = "Sami.khanfir@ba-inmotion.com";
const DEMO_SUBJECT = encodeURIComponent("Demande de démo — BAIM Mission Control");
const DEMO_BODY = encodeURIComponent(
  "Bonjour Sami,\n\n" +
  "J'ai découvert votre plateforme via une déclaration de paiement et je souhaiterais en savoir plus " +
  "sur les capabilités de gestion de CRA, timesheets, facturation et portefeuille missions.\n\n" +
  "Pourriez-vous m'organiser une démo ?\n\nCordialement,"
);
const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${DEMO_SUBJECT}&body=${DEMO_BODY}`;

// Iter-206j — 10 real captures matching the platform's main modules.
// Order requested by the CEO:
//   01 Dashboard, 02 Timesheet & CRA, 03 Portefeuille missions,
//   04 NDF + OCR, 05 BC + dépenses internes, 06 BOT intelligent,
//   07 Facturation client, 08 Espace client paiements, 09 Profitabilité,
//   10 Simulation de mission.
const PUBLIC = process.env.PUBLIC_URL || "";
const SCREENS = [
  { src: `${PUBLIC}/baim-promo/01_dashboard.jpg`,    title: "Dashboard pilotage",            desc: "KPIs unifiés temps réel : taux de réalisation, marge, factures en retard, missions à risque." },
  { src: `${PUBLIC}/baim-promo/02_timesheet.jpg`,    title: "Timesheet & CRA",                desc: "Saisie hebdomadaire, validation manager, export PDF et signature client — sans Excel." },
  { src: `${PUBLIC}/baim-promo/03_missions.jpg`,     title: "Portefeuille missions",          desc: "Missions, staffing, enveloppe budgétaire et alerte dépassement — un seul écran." },
  { src: `${PUBLIC}/baim-promo/04_expenses.jpg`,     title: "Notes de frais & IA OCR",        desc: "Scannez un ticket : Gemini Vision extrait montants, TVA, fournisseur, devise — en quelques secondes." },
  { src: `${PUBLIC}/baim-promo/05_outgoing_pos.jpg`, title: "Bons de commande & dépenses internes", desc: "Émission, envoi par email, réception facture fournisseur OCR-vérifiée, paiement → ligne cabinet auto." },
  { src: `${PUBLIC}/baim-promo/06_chatbot.jpg`,      title: "BOT intelligent",                desc: "Assistant conversationnel Claude pour la navigation, les workflows et la levée des blocages métier." },
  { src: `${PUBLIC}/baim-promo/04_billing.jpg`,      title: "Facturation clients",            desc: "Factures PDF brandées, multi-devises, signature CEO incluse — envoi Gmail en 1 clic." },
  { src: `${PUBLIC}/baim-promo/06_declarations.jpg`, title: "Espace client paiements",        desc: "Liens signés, déclarations clients, accusés de réception PDF auto — relances zéro friction." },
  { src: `${PUBLIC}/baim-promo/05_profitability.jpg`,title: "Gestion de la rentabilité",      desc: "Marge réelle par mission, par collaborateur, par client — calculée en continu." },
  { src: `${PUBLIC}/baim-promo/10_simulations.jpg`,  title: "Simulation de mission",          desc: "Modélisez profils + jours/phase, projetez le chiffre d'affaires et la marge avant la signature." },
];

// Iter-206j — props applied on every <img> to disable downloads/right-click/drag.
const NO_DOWNLOAD_PROPS = {
  draggable: false,
  onContextMenu: (e) => e.preventDefault(),
  onDragStart: (e) => e.preventDefault(),
  style: { userSelect: "none", WebkitTouchCallout: "none", WebkitUserDrag: "none", pointerEvents: "auto" },
};


export function BAIMPoweredByCompact() {
  return (
    <div
      className="mt-8 rounded-xl border p-4 flex items-center gap-4 flex-wrap"
      style={{
        borderColor: BRAND.creamBorder,
        background: "linear-gradient(135deg, #FFFFFF 0%, " + BRAND.creamBg + " 100%)",
      }}
      data-testid="baim-promo-compact"
    >
      <div className="rounded-lg p-2 shrink-0" style={{ background: BRAND.navy }}>
        <Sparkles size={18} color="#fff" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono" style={{ color: BRAND.navy, opacity: 0.6 }}>
          Plateforme développée par BAIM
        </div>
        <div className="text-sm mt-0.5 leading-snug" style={{ color: BRAND.navy }}>
          Vous aussi, gérez vos <strong>CRA</strong>, <strong>timesheets</strong>, <strong>factures</strong> et{" "}
          <strong>portefeuille missions</strong> avec une seule plateforme.
        </div>
      </div>
      <a
        href={DEMO_MAILTO}
        data-testid="baim-promo-compact-cta"
        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md text-xs font-semibold text-white shrink-0 transition-colors"
        style={{ background: BRAND.navy }}
        onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.navyHover)}
        onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.navy)}
      >
        Demander une démo <ArrowRight size={13} />
      </a>
    </div>
  );
}


export function BAIMPoweredByExpanded() {
  const [idx, setIdx] = useState(0);
  // Auto-rotate every 5s, pause on hover.
  const [paused, setPaused] = useState(false);
  // Iter-206j — lightbox state for the click-to-zoom preview.
  const [zoomedIdx, setZoomedIdx] = useState(null);
  useEffect(() => {
    if (paused || zoomedIdx !== null) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), 5000);
    return () => clearInterval(t);
  }, [paused, zoomedIdx]);
  // Keyboard navigation while the lightbox is open (Esc / arrows).
  useEffect(() => {
    if (zoomedIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomedIdx(null);
      if (e.key === "ArrowLeft") setZoomedIdx((i) => (i - 1 + SCREENS.length) % SCREENS.length);
      if (e.key === "ArrowRight") setZoomedIdx((i) => (i + 1) % SCREENS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomedIdx]);

  const cur = SCREENS[idx];

  return (
    <div
      className="mt-8 rounded-2xl overflow-hidden border shadow-sm"
      style={{ borderColor: BRAND.creamBorder, background: "#fff" }}
      data-testid="baim-promo-expanded"
    >
      {/* Header strip */}
      <div
        className="px-6 py-5 flex items-start gap-4 flex-wrap"
        style={{ background: "linear-gradient(135deg, " + BRAND.navy + " 0%, #2A4570 100%)", color: "#fff" }}
      >
        <div className="rounded-lg p-2.5 shrink-0" style={{ background: "rgba(255,255,255,0.12)" }}>
          <Sparkles size={20} color="#fff" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono" style={{ opacity: 0.75 }}>
            Cette plateforme est développée par BAIM
          </div>
          <div className="font-heading text-xl mt-1 leading-tight">
            Pilotez votre cabinet comme nous le faisons.
          </div>
          <div className="text-sm mt-1 leading-relaxed" style={{ opacity: 0.88 }}>
            Une suite intégrée pour la gestion des <strong>CRA</strong>, <strong>timesheets</strong>,{" "}
            <strong>facturation client</strong> et <strong>portefeuille missions</strong> — sans Excel ni outils éclatés.
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div
        className="px-6 pt-6 pb-4"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="relative rounded-lg overflow-hidden border" style={{ borderColor: BRAND.creamBorder, background: BRAND.creamBg }}>
          <button
            type="button"
            onClick={() => setZoomedIdx(idx)}
            className="block w-full p-0 m-0 cursor-zoom-in group/zoom"
            aria-label={`Agrandir la capture : ${cur.title}`}
            data-testid={`baim-promo-zoom-trigger-${idx}`}
          >
            <img
              key={cur.src}
              src={cur.src}
              alt={cur.title}
              className="w-full h-auto block object-cover transition-opacity duration-500 select-none"
              style={{ aspectRatio: "1280 / 716" }}
              data-testid={`baim-promo-screen-${idx}`}
              loading="lazy"
              {...NO_DOWNLOAD_PROPS}
            />
            <span
              className="absolute top-2 right-2 rounded-md px-1.5 py-1 backdrop-blur-sm opacity-0 group-hover/zoom:opacity-100 transition-opacity"
              style={{ background: "rgba(255,255,255,0.92)", color: BRAND.navy }}
            >
              <Maximize2 size={14} />
            </span>
          </button>
          {/* Nav buttons */}
          <button
            type="button"
            onClick={() => setIdx((i) => (i - 1 + SCREENS.length) % SCREENS.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 shadow-md backdrop-blur-sm transition-colors"
            style={{ background: "rgba(255,255,255,0.88)", color: BRAND.navy }}
            aria-label="Précédent"
            data-testid="baim-promo-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % SCREENS.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 shadow-md backdrop-blur-sm transition-colors"
            style={{ background: "rgba(255,255,255,0.88)", color: BRAND.navy }}
            aria-label="Suivant"
            data-testid="baim-promo-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Caption */}
        <div className="mt-3 min-h-[58px]">
          <div
            className="text-[11px] uppercase tracking-[0.2em] font-mono"
            style={{ color: BRAND.orange }}
          >
            {String(idx + 1).padStart(2, "0")} · {String(SCREENS.length).padStart(2, "0")}
          </div>
          <div className="font-heading text-base mt-0.5" style={{ color: BRAND.navy }}>
            {cur.title}
          </div>
          <div className="text-xs mt-1 leading-relaxed" style={{ color: BRAND.navy, opacity: 0.75 }}>
            {cur.desc}
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center gap-1.5 mt-3">
          {SCREENS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Capture ${i + 1}`}
              className="rounded-full transition-all"
              style={{
                width: i === idx ? 22 : 8,
                height: 8,
                background: i === idx ? BRAND.navy : BRAND.creamBorder,
              }}
              data-testid={`baim-promo-dot-${i}`}
            />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div
        className="px-6 py-5 border-t flex items-center gap-4 flex-wrap"
        style={{ borderColor: BRAND.creamBorder, background: BRAND.creamBg }}
      >
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold" style={{ color: BRAND.navy }}>
            Envie de voir ce que ça donne avec vos données ?
          </div>
          <div className="text-xs mt-1 leading-relaxed" style={{ color: BRAND.navy, opacity: 0.75 }}>
            Réservez une démo personnalisée auprès de <strong>Sami Khanfir</strong>, fondateur de BAIM.
            Une démo gratuite, 30 minutes, en visio ou en présentiel à Casablanca.
          </div>
        </div>
        <a
          href={DEMO_MAILTO}
          data-testid="baim-promo-expanded-cta"
          className="inline-flex items-center gap-1.5 px-5 h-11 rounded-md text-sm font-semibold text-white shrink-0 shadow-sm transition-colors"
          style={{ background: BRAND.navy }}
          onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.navyHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.navy)}
        >
          <Mail size={14} />
          Demander ma démo
          <ArrowRight size={14} />
        </a>
      </div>

      {/* Footer email */}
      <div
        className="px-6 py-3 text-[11px] text-center"
        style={{ background: "#fff", color: BRAND.navy, opacity: 0.7, borderTop: `1px solid ${BRAND.creamBorder}` }}
      >
        Contact direct :{" "}
        <a
          href={DEMO_MAILTO}
          className="underline hover:no-underline font-medium"
          style={{ color: BRAND.orange, opacity: 1 }}
        >
          {DEMO_EMAIL}
        </a>
      </div>

      {/* Iter-206j — Lightbox / Zoom preview, opens on screen click */}
      {zoomedIdx !== null && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center backdrop-blur-md p-4 sm:p-8 animate-in fade-in"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={() => setZoomedIdx(null)}
          role="dialog"
          aria-modal="true"
          data-testid="baim-promo-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomedIdx(null); }}
            className="absolute top-4 right-4 rounded-full p-2 text-white hover:bg-white/15 transition-colors"
            aria-label="Fermer"
            data-testid="baim-promo-lightbox-close"
          >
            <X size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomedIdx((i) => (i - 1 + SCREENS.length) % SCREENS.length); }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 rounded-full p-3 text-white hover:bg-white/15 transition-colors"
            aria-label="Précédent"
            data-testid="baim-promo-lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomedIdx((i) => (i + 1) % SCREENS.length); }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 rounded-full p-3 text-white hover:bg-white/15 transition-colors"
            aria-label="Suivant"
            data-testid="baim-promo-lightbox-next"
          >
            <ChevronRight size={28} />
          </button>
          <img
            src={SCREENS[zoomedIdx].src}
            alt={SCREENS[zoomedIdx].title}
            className="max-w-[95vw] max-h-[78vh] w-auto h-auto rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="baim-promo-lightbox-img"
            {...NO_DOWNLOAD_PROPS}
          />
          <div className="mt-4 text-center max-w-2xl px-4">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/60">
              {String(zoomedIdx + 1).padStart(2, "0")} / {String(SCREENS.length).padStart(2, "0")}
            </div>
            <div className="mt-1 text-lg sm:text-xl font-heading text-white">
              {SCREENS[zoomedIdx].title}
            </div>
            <div className="mt-1 text-sm text-white/75 leading-relaxed">
              {SCREENS[zoomedIdx].desc}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
